import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UserGuidesService } from 'src/app/core/services/user-guides.service';
import { UtilService } from 'src/app/core/services/util.service';
import { marked } from 'marked';
import TurndownService from 'turndown';

@Component({
  selector: 'app-user-guide-editor',
  templateUrl: './user-guide-editor.component.html',
  styleUrls: ['./user-guide-editor.component.scss']
})
export class UserGuideEditorComponent implements OnInit {
  action: 'add' | 'edit' = 'add';
  key = '';
  area = 'admin';
  htmlContent = '';
  loading = false;
  submitting = false;

  private turndownService = new TurndownService({
    headingStyle: 'atx',
    codeBlockStyle: 'fenced',
    bulletListMarker: '-',
  });

  quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['blockquote', 'code-block'],
      ['link'],
      [{ align: [] }],
      ['clean'],
    ],
  };

  areaOptions = [
    { label: 'Admin', value: 'admin' },
    { label: 'Applicant', value: 'applicant' },
  ];

  constructor(
    public dialogRef: MatDialogRef<UserGuideEditorComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private guidesService: UserGuidesService,
    private utilService: UtilService,
  ) {
    // Add table rule for turndown
    this.turndownService.addRule('table', {
      filter: ['table'],
      replacement: (_content, node) => {
        return this.tableToMarkdown(node as HTMLElement);
      }
    });
  }

  ngOnInit() {
    this.action = this.data.action;
    if (this.data.data?.area) {
      this.area = this.data.data.area;
    }
    if (this.action === 'edit' && this.data.data) {
      this.key = this.data.data.key;
      this.area = this.data.data.area || 'admin';
      this.loading = true;
      this.guidesService.getGuide(this.key, this.area).subscribe({
        next: (res: any) => {
          const markdown = res.content || '';
          this.htmlContent = marked.parse(markdown) as string;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.utilService.openToastr('error', 'Failed to load guide content');
        }
      });
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];
    const reader = new FileReader();
    reader.onload = () => {
      const markdown = reader.result as string;
      this.htmlContent = marked.parse(markdown) as string;
    };
    reader.readAsText(file);
    input.value = '';
  }

  get isValid(): boolean {
    const textContent = this.htmlContent?.replace(/<[^>]*>/g, '').trim() || '';
    if (this.action === 'add') {
      return this.key.trim().length > 0 && textContent.length > 0;
    }
    return textContent.length > 0;
  }

  private htmlToMarkdown(html: string): string {
    return this.turndownService.turndown(html);
  }

  private tableToMarkdown(tableEl: HTMLElement): string {
    const rows = tableEl.querySelectorAll('tr');
    if (!rows.length) return '';

    const result: string[] = [];
    rows.forEach((row, rowIndex) => {
      const cells = row.querySelectorAll('th, td');
      const cellTexts = Array.from(cells).map(c => (c.textContent || '').trim());
      result.push('| ' + cellTexts.join(' | ') + ' |');
      if (rowIndex === 0) {
        result.push('| ' + cellTexts.map(() => '---').join(' | ') + ' |');
      }
    });
    return '\n' + result.join('\n') + '\n';
  }

  save() {
    if (!this.isValid || this.submitting) return;
    this.submitting = true;

    const markdownContent = this.htmlToMarkdown(this.htmlContent);

    if (this.action === 'add') {
      const payload = {
        key: this.key.trim(),
        content: markdownContent,
        area: this.area,
      };
      this.guidesService.createGuide(payload).subscribe({
        next: () => {
          this.utilService.openToastr('success', 'User guide created');
          this.dialogRef.close(true);
        },
        error: () => {
          this.submitting = false;
          this.utilService.openToastr('error', 'Failed to create user guide');
        }
      });
    } else {
      this.guidesService.updateGuide(this.key, markdownContent, this.area).subscribe({
        next: () => {
          this.utilService.openToastr('success', 'User guide updated');
          this.dialogRef.close(true);
        },
        error: () => {
          this.submitting = false;
          this.utilService.openToastr('error', 'Failed to update user guide');
        }
      });
    }
  }

  close() {
    this.dialogRef.close();
  }
}
